"use client";
import { Track } from "@/src/lib/tracking";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/useRequestPortfolioActions";

import { useCallback } from "react";
import {
  REQUEST_PORTFOLIO_ACTION,
  type RequestPortfolioCardAction,
} from "@components/ChatOnboarding/requestPortfolioAction";
import { openPortfolioScreenshotModal } from "@components/PortfolioScreenshotModal";
// import { useMemoizedFn } from "ahooks";
import { OspToast } from "@ospweb/sdk-ui";
import { IS_PLAID_OPEN } from "@components/PlaidImportModal/openPlaidImportModal";
import type { SenderAttachment } from "@components/Sender/type";
import { useAccountConnectionFlow } from "@/src/hooks/useAccountConnectionFlow";

const I18n = I18nUtil.createScoped(Translations);

export { REQUEST_PORTFOLIO_ACTION as REQUEST_PORTFOLIO_SUBMIT_ACTION };

const REQUEST_PORTFOLIO_SKIP_CONTENT = JSON.stringify({
  action: REQUEST_PORTFOLIO_ACTION.SKIPPED,
});

const REQUEST_PORTFOLIO_PLAID_SUCCESS_MESSAGE =
  "Plaid account connected successfully. Asset data may be available in 2-3 minutes.";

// const REQUEST_PORTFOLIO_PLAID_ERROR_MESSAGE =
//   "Failed to connect Plaid account.";

export interface UseRequestPortfolioActionsOptions {
  onSubmit: (
    content: string,
    options?: { attachments?: SenderAttachment[] },
  ) => void;
}

const submitPortfolioModalResult = (
  onSubmit: UseRequestPortfolioActionsOptions["onSubmit"],
  chatInfo: Record<string, unknown>,
  attachments: SenderAttachment[],
) => {
  onSubmit(
    JSON.stringify(chatInfo),
    attachments.length > 0 ? { attachments } : undefined,
  );
};

export const useRequestPortfolioActions = ({
  onSubmit,
}: UseRequestPortfolioActionsOptions) => {
  const handlePortfolioModal = useCallback(
    async (uploadType: "image" | "pdf") => {
      const result = await openPortfolioScreenshotModal({ uploadType });
      if (result.action !== "confirm") {
        return;
      }

      try {
        submitPortfolioModalResult(
          onSubmit,
          result.chatInfo,
          result.attachments,
        );
      } catch (err) {
        Track.log(
          "warn",
          "useRequestPortfolioActions",
          "portfolio modal tool message failed",
          {
            upload_type: String(uploadType),
            cause: String(err),
          },
        );
      }
    },
    [onSubmit],
  );

  // 使用账户连接编排 hook
  const { startFlow } = useAccountConnectionFlow({
    onBindSuccess: async () => {
      onSubmit(
        JSON.stringify({
          action: REQUEST_PORTFOLIO_ACTION.CONNECT_PLAID,
          message: REQUEST_PORTFOLIO_PLAID_SUCCESS_MESSAGE,
        }),
      );
    },
    paywallCopyOverrides: {
      title: I18n.t("Answers from your real numbers. Unlock full access."),
      subtitle: I18n.t(
        "Link an account and I'll answer with your actual data.",
      ),
      features: [
        I18n.t("Track holdings, prices and returns in real time"),
        I18n.t("Net worth, spending and accounts in one place"),
        I18n.t("Flag risks before they land"),
      ],
    },
  });

  // const handlePlaidConnect = useMemoizedFn(async () => {
  //   await startFlow();
  // });

  const handleAction = useCallback(
    async (action: RequestPortfolioCardAction) => {
      switch (action) {
        case REQUEST_PORTFOLIO_ACTION.CONNECT_PLAID:
          if (!IS_PLAID_OPEN) {
            OspToast.info({
              content: I18n.t("Coming soon, please choose other ways."),
            });
            return;
          }
          await startFlow();
          return;
        case REQUEST_PORTFOLIO_ACTION.SCREENSHOT_RECOGNIZED:
          await handlePortfolioModal("image");
          return;
        case REQUEST_PORTFOLIO_ACTION.PDF_RECOGNIZED:
          await handlePortfolioModal("pdf");
          return;
        case REQUEST_PORTFOLIO_ACTION.SKIPPED:
          onSubmit(REQUEST_PORTFOLIO_SKIP_CONTENT);
          return;
        default:
          return;
      }
    },
    [startFlow, handlePortfolioModal, onSubmit],
  );

  return {
    handleAction,
  };
};
